var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, DataTable, FlexBox, Icon, Page, } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { billingApi } from 'apis/billing';
import { appActions } from 'app/app.actions';
import { Sentry } from 'app/utils/sentry';
import { useNotifications } from 'hooks/useNotifications';
import { getCreditCardAttributes } from 'utils/billing';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { AddPaymentMethodModal } from './components/addPaymentMethodModal/addPaymentMethodModal';
import { CreditCardImage } from './components/creditCardImage';
import { isCreditCardExpired } from './utils/isCreditCardExpired';
import { useConfirmPaymentMethod } from './utils/useConfirmPaymentMethod';
export const BillingPaymentMethodsSection = () => {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const { paymentMethods, isError, isFetching, refetch } = billingApi.useReadPaymentMethodsQuery(undefined, {
        selectFromResult: (_a) => {
            var { data } = _a, rest = __rest(_a, ["data"]);
            return (Object.assign(Object.assign({}, rest), { paymentMethods: data === null || data === void 0 ? void 0 : data.data.payment_methods }));
        },
    });
    const [updateDefaultPaymentMethod, { isLoading: isUpdateDefaultPaymentMethodLoading, originalArgs: updateDefaultPaymentMethodOriginalArgs, },] = billingApi.useUpdateDefaultPaymentMethodMutation();
    const [deletePaymentMethodMutation, { isLoading: isDeletePaymentMethodLoading, originalArgs: deletePaymentMethodOriginalArgs, },] = billingApi.useDeletePaymentMethodMutation();
    const [confirmPaymentMethod, { isLoading: isConfirmingPaymentMethod }] = useConfirmPaymentMethod();
    const openAddPaymentMethodModal = () => __awaiter(void 0, void 0, void 0, function* () {
        const { paymentId } = yield dispatch(appActions.showModal({
            modal: { type: 'settings:addPaymentMethod', arg: {} },
        }));
        if (paymentId) {
            requestConfirmPaymentMethod(paymentId);
            refetch();
        }
    });
    const handleOnDeletePaymentMethod = (id) => __awaiter(void 0, void 0, void 0, function* () {
        const { isConfirmed } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:confirm',
                arg: {
                    title: `Delete card`,
                    description: 'Are you sure you want to delete this card?',
                    confirm: {
                        label: 'Delete',
                    },
                },
            },
        }));
        if (!isConfirmed) {
            return;
        }
        try {
            yield deletePaymentMethodMutation({ id });
            notify({
                title: 'Successfully deleted payment method',
                icon: 'done',
                className: 'success',
                time: 3000,
                showDesktop: false,
            });
        }
        catch (error) {
            notify({
                title: 'Failed to delete payment method',
                text: `Please try again or contact support.`,
                icon: 'exclamation',
                className: 'danger',
                time: 5000,
                showDesktop: false,
            });
        }
    });
    const handleOnUpdateDefaultPaymentMethod = (id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield updateDefaultPaymentMethod({ id }).unwrap();
            refetch();
            notify({
                title: 'Successfully updated default payment method',
                icon: 'done',
                className: 'success',
                time: 3000,
                showDesktop: false,
            });
        }
        catch (error) {
            notify({
                title: 'Failed to update default payment method',
                text: `Please try again or contact support.`,
                icon: 'exclamation',
                className: 'danger',
                time: 5000,
                showDesktop: false,
            });
        }
    });
    const requestConfirmPaymentMethod = (id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield confirmPaymentMethod(id);
            refetch();
            notify({
                title: 'Payment method successfully authorized',
                icon: 'done',
                className: 'success',
                time: 3000,
                showDesktop: false,
            });
        }
        catch (error) {
            Sentry.captureException(error);
            notify({
                title: 'Failed to authorize payment method',
                text: `Please try again or contact support.`,
                icon: 'exclamation',
                className: 'danger',
                time: 5000,
                showDesktop: false,
            });
        }
    });
    return (_jsxs(Page.Section, Object.assign({ title: "Payment methods", controls: _jsx(Button, Object.assign({ variant: "primary", onClick: () => openAddPaymentMethodModal() }, { children: "Add payment method" })) }, { children: [_jsx(DataTable, { tableStatusProps: {
                    loading: isFetching,
                    loadingFailed: isError,
                    emptyState: !isFetching && !isError && !(paymentMethods === null || paymentMethods === void 0 ? void 0 : paymentMethods.length)
                        ? {
                            title: 'There are no payment methods configured',
                            description: `Add a payment method for automatic payments, or contact support.`,
                        }
                        : undefined,
                }, data: paymentMethods !== null && paymentMethods !== void 0 ? paymentMethods : [], columns: [
                    {
                        label: 'Type',
                        selector: (item) => (_jsxs(FlexBox, Object.assign({ gap: "300", alignItems: "center" }, { children: [item.is_default ? (_jsx(Icon, { name: "done", color: typography.color.success, size: 24 })) : (_jsx(Box, { width: "24px" })), _jsx(CreditCardImage, { width: "40", style: {
                                        aspectRatio: '3 / 2',
                                    }, creditCard: item.cc_details.cc_type }), _jsx("span", { children: item.cc_details.cc_type })] }))),
                        style: { width: '240px' },
                    },
                    {
                        label: 'Card number',
                        selector: (item) => (_jsx(Box, { children: `${getCreditCardAttributes(item.cc_details.cc_type)
                                .anonymizedPattern} ${item.cc_details.cc_last_4}` })),
                        style: { width: '200px' },
                    },
                    {
                        label: 'Expiration date',
                        selector: (item) => (_jsx(Box, Object.assign({ color: isCreditCardExpired(Number(item.cc_details.cc_expiration_month), Number(item.cc_details.cc_expiration_year))
                                ? typography.color.error
                                : typography.color.success }, { children: `${item.cc_details.cc_expiration_month} / ${item.cc_details.cc_expiration_year}` }))),
                        style: { width: '140px' },
                    },
                    {
                        label: 'Status',
                        selector: (item) => (_jsx(Box, Object.assign({ as: "span", color: item.is_authorized
                                ? typography.color.success
                                : typography.color.error }, { children: item.is_authorized ? 'Authorized' : 'Needs authorization' }))),
                        style: { width: 'auto' },
                    },
                    {
                        label: '',
                        style: { width: '0px' },
                        selector: (item) => (_jsxs(FlexBox, Object.assign({ gap: "200", justifyContent: "flex-end" }, { children: [!item.is_authorized && (_jsx(Button, Object.assign({ variant: "secondary", onClick: () => requestConfirmPaymentMethod(item.external_key), isLoading: isConfirmingPaymentMethod }, { children: "Authorize card" }))), !item.is_default && (_jsx(Button, Object.assign({ variant: "secondary", onClick: () => handleOnUpdateDefaultPaymentMethod(item.id), isLoading: isUpdateDefaultPaymentMethodLoading &&
                                        (updateDefaultPaymentMethodOriginalArgs === null || updateDefaultPaymentMethodOriginalArgs === void 0 ? void 0 : updateDefaultPaymentMethodOriginalArgs.id) === item.id }, { children: "Use as default card" }))), _jsx(Button, Object.assign({ variant: "secondary", onClick: () => handleOnDeletePaymentMethod(item.id), isLoading: isDeletePaymentMethodLoading &&
                                        (deletePaymentMethodOriginalArgs === null || deletePaymentMethodOriginalArgs === void 0 ? void 0 : deletePaymentMethodOriginalArgs.id) === item.id }, { children: "Delete card" }))] }))),
                    },
                ] }), _jsx(AddPaymentMethodModal, {})] })));
};
